import {Choice, Dialog, Textarea, TextInput} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import type {DeclineOfferRequestBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

import type {DeclineSource} from '../../model/auctionView.types';

const SOURCES: DeclineSource[] = ['IN_PERSON', 'PHONE', 'EMAIL', 'SIGNED_DOCUMENT'];
// The BE refuses these sources without an evidence reference (EVIDENCE_REQUIRED).
const NEEDS_EVIDENCE: DeclineSource[] = ['EMAIL', 'SIGNED_DOCUMENT'];

interface DeclineOfferDialogProps {
  isOpen: boolean;
  offerId: string;
  traderName: string;
  onClose: () => void;
  // Resolves to false when the request fails. The caller shows the error, the dialog keeps the input.
  onSave: (body: DeclineOfferRequestBody) => Promise<boolean>;
}

export function DeclineOfferDialog(props: DeclineOfferDialogProps) {
  const [source, setSource] = useState<DeclineSource | null>(null);
  const [evidence, setEvidence] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isEvidenceMissing = source !== null && NEEDS_EVIDENCE.includes(source) && !evidence?.trim();

  const close = () => {
    setSource(null);
    setEvidence(null);
    setNote(null);
    setIsSubmitted(false);
    props.onClose();
  };

  const save = async () => {
    setIsSubmitted(true);
    if (source === null || isEvidenceMissing) {
      return;
    }
    setIsSaving(true);
    const isSaved = await props.onSave({offerId: props.offerId, source, evidence, note});
    setIsSaving(false);
    if (isSaved) {
      close();
    }
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      size="small"
      title={i18n.t('entity.auction.decline.title')}
      onClose={close}
      buttons={[
        {title: i18n.t('general.actions.cancel'), variant: 'secondary', onClick: close},
        {
          title: i18n.t('entity.auction.decline.save'),
          variant: 'primary',
          isLoading: isSaving,
          onClick: () => void save(),
          'data-testid': 'auction-decline-save',
        },
      ]}
      data-testid="auction-decline-dialog"
    >
      <VStack spacing={4}>
        <Text color="secondary">
          {i18n.t('entity.auction.decline.note', {dealer: props.traderName})}
        </Text>
        <Choice<DeclineSource>
          label={i18n.t('entity.auction.decline.source')}
          isRequired
          value={source}
          onChange={setSource}
          errorMessage={
            isSubmitted && source === null ? i18n.t('general.validations.fieldIsRequired') : null
          }
          options={SOURCES.map((value) => ({
            value,
            label: i18n.t(`entity.auction.decline.sources.${value}`),
          }))}
          data-testid="auction-decline-source"
        />
        <TextInput
          label={i18n.t('entity.auction.decline.evidence')}
          isRequired={isEvidenceMissing}
          value={evidence}
          onChange={setEvidence}
          placeholder={i18n.t('entity.auction.decline.evidencePlaceholder')}
          errorMessage={
            isSubmitted && isEvidenceMissing
              ? i18n.t('entity.auction.decline.evidenceRequired')
              : null
          }
          data-testid="auction-decline-evidence"
        />
        <Textarea
          label={i18n.t('entity.auction.decline.customerNote')}
          value={note}
          onChange={setNote}
          placeholder={i18n.t('entity.auction.decline.customerNotePlaceholder')}
          data-testid="auction-decline-note"
        />
      </VStack>
    </Dialog>
  );
}
